// @ts-strict-ignore
import React, { useRef, useState } from 'react'
import { IonGrid, IonRow, IonCol } from '@ionic/react'

import { PlayerStatus, AudioPlayerContainer as Container, PlaylistItem, formatTimeMS } from 'mytours-read-common'

import { getDirMode, waitForSomething } from '../../../platform'

import { formatPlayPauseLabel } from '../../functions'
import { useAudioPlayerSpace } from '../../useAudioPlayerSpace'
import {
	getCurrentSound,
	onBack,
	onForward,
	sendAnalyticsEvent,
	seekToPercentage,
	DEFAULT_SKIP_SECONDS,
} from '../../soundController'
import { EventActionsEnum } from '../../../logging/analytics'

import FullscreenAudioPlayer from './FullscreenAudioPlayer'
import AudioSeekBar from './AudioSeekBar'
import { Button, Text } from '@mytours/components'

import { containerClose, containerDisappear, containerOpen } from '../animations'

import strings from '../../../../strings'

import classNames from 'classnames'
import './styles/style.less'
import { useEffect } from 'react'
import usePrevious from 'common_lib/hooks/use-previous'
import { useMemo } from 'react'
import { deleteSectionsQueryParams, getSectionsQueryParams } from 'mobile_web/modules/navigation/queryParams'

interface Props {
	className?: string
	bottomOffset?: number
}

const AudioPlayer: React.FC<Container.Props & Container.Actions & Props> = props => {
	const [showFullScreenPlayer, setShowFullScreenPlayer] = useState<boolean>(false)
	const [animationProcess, setAnimationProcess] = useState<boolean>(false)

	const audioWrapperRef = useRef<HTMLDivElement>()
	const audioWrapperRefNode = audioWrapperRef?.current

	const audioRef = useRef<HTMLIonGridElement>()
	const audioRefNode = audioRef?.current

	const prevCurrentAudio: PlaylistItem = usePrevious(props.currentAudio)

	// Read by onSeek at seek time rather than render time: a drag release can race
	// a track switch, and a render-time closure would seek the previous track's
	// sound, which stays registered in stqryAudioFiles. Mirrors the fullscreen
	// player's fresh this.props read.
	const currentAudioRef = useRef(props.currentAudio)
	currentAudioRef.current = props.currentAudio

	const bottomOffset = useMemo<number>(() => (props.bottomOffset > -1 ? props.bottomOffset : 56), [props.bottomOffset])

	const showAudioPlayer = useMemo<boolean>(() => {
		const { playerStatus } = props.currentAudio || {}
		if (playerStatus !== PlayerStatus.Pause && playerStatus !== PlayerStatus.Playing) {
			return false
		}

		return true
	}, [props.currentAudio?.playerStatus])

	const isPlaying = useMemo<boolean>(
		() => props.currentAudio?.playerStatus === PlayerStatus.Playing,
		[props.currentAudio?.playerStatus],
	)

	const wrapperStyles =
		showFullScreenPlayer || animationProcess
			? {
					paddingBottom: bottomOffset + 14,
				}
			: {}

	const toggleFullScreenPlayerModal = (): void => {
		setAnimationProcess(true)
		setShowFullScreenPlayer(!showFullScreenPlayer)
	}

	const onGoFullscreenPlayer = (): void => {
		const { currentAudio, collection } = props

		if (!showFullScreenPlayer) {
			sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioOpenPlayer)
		}

		toggleFullScreenPlayerModal()
	}

	const openFullScreenAudio = (): void => {
		const { audioTrack } = props.currentAudio || {}

		if (audioTrack?.coverImages?.length > 0 && (audioTrack.slideshowEnabled || audioTrack.fullScreenEnabled)) {
			onGoFullscreenPlayer()
		}
	}

	useEffect(() => {
		if (props?.currentAudio?.playerStatus === PlayerStatus.Ended) {
			setShowFullScreenPlayer(false)
		}
	}, [props.currentAudio])

	useEffect(
		() => () => {
			setShowFullScreenPlayer(false)
		},
		[],
	)

	useEffect(() => {
		if (prevCurrentAudio?.audioTrack?.audio?.id === props.currentAudio?.audioTrack?.audio?.id) return // audio should be loaded only once

		const { storySectionId, sectionItemId } = getSectionsQueryParams()

		if (!storySectionId || !sectionItemId) return

		const { currentAudio } = props
		const { audioTrack, container } = currentAudio
		const { file, id: audioId } = audioTrack?.audio || {}
		const { id: fileId } = file

		if (sectionItemId !== audioId || storySectionId !== container?.id) return

		waitForSomething(() => getCurrentSound(audioId, container, fileId)).then(() => {
			onGoFullscreenPlayer()
			deleteSectionsQueryParams()
		})
	}, [props.currentAudio, props.container])

	useAudioPlayerSpace(showAudioPlayer)

	useEffect(() => {
		if (showFullScreenPlayer) {
			containerOpen(audioRefNode, bottomOffset)
				.onFinish(() => {
					setAnimationProcess(false)
				})
				.play()
		} else {
			containerClose(audioRefNode, bottomOffset)
				.onFinish(() => {
					setAnimationProcess(false)
				})
				.play()
		}
	}, [showFullScreenPlayer])

	useEffect(() => {
		const { playerStatus } = props.currentAudio || {}

		const isActive =
			prevCurrentAudio?.playerStatus !== playerStatus &&
			playerStatus === PlayerStatus.Playing &&
			prevCurrentAudio?.playerStatus !== PlayerStatus.Pause

		if (isActive) {
			openFullScreenAudio()
		}
	}, [props.currentAudio])

	const onPlayPause = (): void => {
		const { playerStatus, audioTrack, container } = props.currentAudio || {}
		const { audio } = audioTrack || {}
		const currentSound = getCurrentSound(audio?.id, container, audio?.file?.id)

		if (currentSound && audio) {
			switch (playerStatus) {
				case PlayerStatus.Playing:
					currentSound.pause()
					return
				case PlayerStatus.Pause:
					currentSound.play()
					return
			}
		}
	}

	const onClickBack = (): void => {
		const { setCurrentTime, currentAudio, collection } = props

		const { audioTrack, container } = currentAudio || {}
		const { audio } = audioTrack || {}

		sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioSkipBackward)
		onBack(audio?.id, container, audio?.file?.id, DEFAULT_SKIP_SECONDS, time => {
			setCurrentTime(audio?.id, time, container, audio?.file?.id)
		})
	}

	const onClickForward = (skipSeconds: number): void => {
		const { setCurrentTime, currentAudio, collection } = props

		const { duration, audioTrack, container } = currentAudio || {}
		const { audio } = audioTrack || {}

		sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioSkipForward)
		onForward(audio?.id, duration, container, audio?.file?.id, skipSeconds, time => {
			setCurrentTime(audio?.id, time, container, audio?.file?.id)
		})
	}

	const onStopSound = (): void => {
		const { audioTrack, container } = props.currentAudio || {}
		const { audio } = audioTrack || {}

		const currentSound = getCurrentSound(audio?.id, container, audio?.file?.id)

		containerDisappear(audioWrapperRefNode)
			.onFinish(() => {
				if (currentSound) {
					currentSound.stop()
				}

				setShowFullScreenPlayer(false)
			})
			.play()
	}

	const renderFullScreenAudioPlayer = (): JSX.Element => {
		const { currentAudio, collection } = props

		return (
			<FullscreenAudioPlayer
				collection={collection}
				currentAudio={currentAudio}
				onPlayPause={onPlayPause}
				onBack={onClickBack}
				onForward={() => onClickForward(DEFAULT_SKIP_SECONDS)}
			/>
		)
	}

	const { currentTime, duration, audioTrack } = props.currentAudio || {}
	const title = audioTrack?.audio?.title || ''

	const renderAudioBarTop = (): JSX.Element => {
		return (
			<>
				{/* Always render the title row — even when the audio has no title — so the
				    bar reserves the same vertical space it does for titled audio. Without
				    this the mini bar collapses to a thin strip when the title is empty,
				    which doesn't match the design (see Tour - 8.pdf in the Singapore
				    audio-guide mockups). */}
				<IonRow className="title">
					<IonCol>
						<Text variant="inherit">{title}</Text>
					</IonCol>
				</IonRow>
				{/* dir="ltr": the audio timeline flows left→right regardless of locale,
				    matching the fullscreen .time-progress-row. */}
				<IonRow className="progress-row" dir="ltr">
					<Text variant="inherit" className="time-label">
						{formatTimeMS(currentTime || 0)}
					</Text>
					<AudioSeekBar
						className="audio-seek-bar audio-seek-bar--mini"
						currentTime={currentTime}
						duration={duration}
						ariaLabel={strings.accessibility_audio_seek}
						onSeek={pct => {
							// Fresh values at seek time — see currentAudioRef above.
							// seekToPercentage no-ops on a missing audio/container.
							const { duration, audioTrack, container } = currentAudioRef.current || {}
							const audio = audioTrack?.audio
							seekToPercentage(audio?.id, pct, duration, container, audio?.file?.id)
						}}
					/>
					<Text variant="inherit" className="time-label">
						{formatTimeMS(duration || 0)}
					</Text>
				</IonRow>
			</>
		)
	}

	const renderAudioBarControls = (isPlaying: boolean): JSX.Element => {
		return (
			<>
				<IonCol>
					<Button
						variant="clear"
						className="time-controls-btn"
						onClick={onClickBack}
						aria-label={
							strings.formatString(strings.accessibility_seek_backward, { seconds: DEFAULT_SKIP_SECONDS }) as string
						}
					>
						<i
							className="fak fa-skip-back-10"
							aria-label={
								strings.formatString(strings.accessibility_seek_backward, { seconds: DEFAULT_SKIP_SECONDS }) as string
							}
						/>
					</Button>
				</IonCol>
				<IonCol>
					<Button
						variant="clear"
						onClick={onPlayPause}
						className={classNames('play-pause-btn', isPlaying ? 'pause-controls-btn' : 'play-controls-btn')}
						aria-label={formatPlayPauseLabel(isPlaying, title)}
						// `ariaLabel` won't change unless we remount
						key={formatPlayPauseLabel(isPlaying, title)}
					>
						<i className={`fas fa-${isPlaying ? 'pause' : 'play'}`} />
					</Button>
				</IonCol>
				<IonCol>
					<Button
						variant="clear"
						className="time-controls-btn"
						onClick={() => onClickForward(DEFAULT_SKIP_SECONDS)}
						aria-label={
							strings.formatString(strings.accessibility_seek_forward, { seconds: DEFAULT_SKIP_SECONDS }) as string
						}
					>
						<i
							className="fak fa-skip-forward-10"
							aria-label={
								strings.formatString(strings.accessibility_seek_forward, { seconds: DEFAULT_SKIP_SECONDS }) as string
							}
						/>
					</Button>
				</IonCol>
			</>
		)
	}

	if (!showAudioPlayer) {
		return null
	}

	return (
		<div className="audio-player-wrapper audio-player-v2" ref={audioWrapperRef} dir={getDirMode()}>
			<div className={classNames('audio-player-inner', { fixed: animationProcess || showFullScreenPlayer })}>
				<IonGrid
					className={classNames(props.className, 'audio-player audio-player-bar', { open: showFullScreenPlayer })}
					style={wrapperStyles}
					ref={audioRef}
				>
					<IonRow className="audio-player-row">
						{!showFullScreenPlayer && renderAudioBarTop()}
						<IonRow className="actions">
							<IonCol>
								<Button
									variant="clear"
									onClick={onGoFullscreenPlayer}
									aria-label={strings.accessibility_open_fullscreen_player}
								>
									<i className={`far fa-${showFullScreenPlayer ? 'chevron-down' : 'chevron-up'}`} />
								</Button>
							</IonCol>
							{/* Always render the title col in fullscreen — even when the title is empty
							    — so the chevron/close col are pushed to opposite edges by a middle
							    spacer. Without it the actions row has only the chevron + close cols
							    (both flex: 0 0 auto), and IonRow's default flex layout stacks them
							    next to each other on the left, pulling the close button out of line
							    with the chevron. */}
							{showFullScreenPlayer && (
								<IonCol className="fullscreen-header-title">
									<Text variant="inherit">{title}</Text>
								</IonCol>
							)}
							{!showFullScreenPlayer && renderAudioBarControls(isPlaying)}
							<IonCol>
								<Button variant="clear" onClick={onStopSound} aria-label={strings.accessibility_finish_audio_track}>
									<i className="fal fa-xmark" />
								</Button>
							</IonCol>
						</IonRow>

						{showFullScreenPlayer && !animationProcess && renderFullScreenAudioPlayer()}
					</IonRow>
				</IonGrid>
			</div>
		</div>
	)
}

const AudioPlayerBarComponent = Container.default(AudioPlayer)
export default AudioPlayerBarComponent as any
